/**
 * As linhas do editor de catálogo e a conversão de/para `metadata.models` (AB#85605).
 *
 * Fora do componente porque são regra, não interface: o que pode ser salvo, o que é
 * preservado do que a tela não edita, e o que impede a gravação.
 */
/** As rotas do gateway, que é o que `metadata.models[<modelo>].api` declara. */
export type ModelRoute = "chat" | "responses" | "embedding" | "transcription";

export type ModelEntry = {
  /**
   * Identidade da LINHA na tela, não do modelo (AB#85605).
   *
   * A lista é editável, e o nome muda a cada tecla: usá-lo como `key` fazia o React trocar
   * a identidade do campo a cada caractere, remontar o input e devolver o foco ao diálogo —
   * era impossível digitar um nome inteiro. O id nasce com a linha e não é persistido.
   */
  id: string;
  /** A chave do catálogo: no Azure, o nome do deployment. */
  name: string;
  api: ModelRoute | "";
  api_version?: string;
  dimensions?: string;
  /** Modelo que o provedor informou servir naquele deployment — leitura, não edição. */
  model?: string;
  /** Tudo o que a tela não edita (cost_source, provider, endpoint…). */
  rest: Record<string, unknown>;
};

/** A allowlist das rotas. Lista e tipo saem daqui, para nao divergirem. */
const ROTAS_VALIDAS = ["chat", "responses", "embedding", "transcription"] as const satisfies
  readonly ModelRoute[];

export const ROTAS: { value: ModelEntry["api"]; label: string }[] = ROTAS_VALIDAS.map(
  (value) => ({ value, label: value })
);

/**
 * Rota declarada no cadastro → rota que a tela aceita.
 *
 * Um `as` direto deixava passar qualquer string: o cadastro é editável, e um valor fora da
 * lista chegaria tipado como rota válida e só falharia no gateway. Fora da allowlist vira
 * vazio, que a validação já trata como rota ausente.
 */
function paraRota(valor: unknown): ModelEntry["api"] {
  return typeof valor === "string" && (ROTAS_VALIDAS as readonly string[]).includes(valor)
    ? (valor as ModelRoute)
    : "";
}

/**
 * Dimensão do vetor: inteiro positivo, ou nada.
 *
 * `Number("abc")` devolve `NaN`, e `NaN` gravado em `metadata` não é recusado por ninguém —
 * some na serialização e reaparece como vetor de tamanho indefinido na indexação, longe da
 * tela onde o valor foi digitado (AB#85518).
 */
export function paraDimensoes(valor: string | undefined): number | undefined {
  const texto = valor?.trim();
  if (!texto || !/^\d+$/.test(texto)) return undefined;
  const numero = Number(texto);
  return Number.isSafeInteger(numero) && numero > 0 ? numero : undefined;
}

/** Identidade de linha, estável enquanto o diálogo está aberto. */
export function novaLinhaDeModelo(): ModelEntry {
  return { id: proximoId(), name: "", api: "chat", rest: {} };
}

let contador = 0;
function proximoId(): string {
  contador += 1;
  return `model-${contador}`;
}

/** `metadata.models` → linhas do editor. */
export function modelsToEntries(models: unknown): ModelEntry[] {
  if (!models || typeof models !== "object" || Array.isArray(models)) return [];
  return Object.entries(models as Record<string, unknown>).map(([name, raw]) => {
    const entry = (raw && typeof raw === "object" && !Array.isArray(raw)
      ? (raw as Record<string, unknown>)
      : {}) as Record<string, unknown>;
    const { api, api_version: apiVersion, dimensions, model, ...rest } = entry;
    return {
      id: proximoId(),
      name,
      api: paraRota(api),
      api_version: typeof apiVersion === "string" ? apiVersion : undefined,
      dimensions: dimensions === undefined || dimensions === null ? undefined : String(dimensions),
      model: typeof model === "string" ? model : undefined,
      rest,
    };
  });
}

/** Linhas do editor → `metadata.models`, preservando o que não é editado. */
export function entriesToModels(entries: ModelEntry[]): Record<string, unknown> {
  const models: Record<string, unknown> = {};
  for (const entry of entries) {
    const nome = entry.name.trim();
    if (!nome) continue;
    const dimensoes = paraDimensoes(entry.dimensions);
    models[nome] = {
      ...entry.rest,
      ...(entry.model ? { model: entry.model } : {}),
      api: entry.api,
      ...(entry.api_version?.trim() ? { api_version: entry.api_version.trim() } : {}),
      ...(dimensoes !== undefined ? { dimensions: dimensoes } : {}),
    };
  }
  return models;
}

/**
 * O que impede salvar, por modelo.
 *
 * Nome e rota são obrigatórios porque é deles que sai o roteamento: modelo sem rota cai em
 * `chat` por omissão, e um modelo de embedding chamado ali volta um erro que parece do
 * provedor. Dimensão obrigatória no embedding porque a base de conhecimento guarda vetor de
 * tamanho fixo (AB#85518).
 *
 * O retorno nomeia O MODELO e A CAUSA: a mensagem genérica mandava procurar qual linha, numa
 * lista que passa de trinta em algumas instalações.
 */
export type ModelProblem = {
  model: string;
  reason: "emptyName" | "duplicate" | "missingApi" | "missingDimensions" | "invalidDimensions";
};

/**
 * Formato de `api-version` que destoa do padrão de data — AVISO, não impedimento.
 *
 * Um caractere a menos não muda a cara do valor, mas o provedor responde a ele com o mesmo
 * 404 "Resource not found" de deployment inexistente: `025-04-01-preview` gravado no lugar de
 * `2025-04-01-preview` mandou procurar o erro no nome do deployment e na rota, que estavam
 * certos. Sinalizar no campo custa nada e encurta essa caçada.
 *
 * Não bloqueia a gravação de propósito: o padrão `AAAA-MM-DD[-preview]` é do Azure, e um
 * provedor que versione de outro jeito não pode ficar impedido de ser cadastrado por uma
 * convenção que não é dele.
 */
export function apiVersionSuspeita(valor: string | undefined): boolean {
  const texto = valor?.trim();
  if (!texto) return false;
  return !/^\d{4}-\d{2}-\d{2}(-preview)?$/.test(texto);
}

export function validateEntries(entries: ModelEntry[]): ModelProblem[] {
  const problemas: ModelProblem[] = [];
  const vistos = new Set<string>();

  for (const entry of entries) {
    const nome = entry.name.trim();
    if (!nome) {
      problemas.push({ model: "", reason: "emptyName" });
      continue;
    }
    if (vistos.has(nome)) problemas.push({ model: nome, reason: "duplicate" });
    vistos.add(nome);
    if (!entry.api) problemas.push({ model: nome, reason: "missingApi" });
    const dimensoesDigitadas = entry.dimensions?.trim();
    if (entry.api === "embedding" && !dimensoesDigitadas) {
      problemas.push({ model: nome, reason: "missingDimensions" });
    } else if (dimensoesDigitadas && paraDimensoes(dimensoesDigitadas) === undefined) {
      // Vale para qualquer rota: dimensao digitada e invalida e erro, nao campo em branco.
      problemas.push({ model: nome, reason: "invalidDimensions" });
    }
  }
  return problemas;
}

